import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal, Field } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { addCode, getCodeFrame, getInstrument, listPolls, listVersions } from './api'
import { PollSelect } from './PollSelect'
import { count, pct } from './vocab'

/**
 * The coding frame that replaces the Text Analytics tab.
 *
 * ## What that tab was
 *
 * PollSphere's Text Analytics NEVER FETCHES ANYTHING. It renders seed constants
 * with fabricated provenance — "GLM-4 · 2,841 tokens", "BERTopic", "Presidio",
 * "fastText lid.176 · 98% F1 on Swahili" — alongside fake flagged responses with
 * confidence scores and eleven inert buttons.
 *
 * ## What this is
 *
 * A human-defined code list, an assign-codes call, an agreement statistic over
 * the answers more than one person coded, and a verbatim export. Fewer promises,
 * all of them kept.
 *
 * The agreement figure is null — with a sentence — when nothing has been coded
 * twice, rather than defaulting to a comfortable number. A frame two coders read
 * differently produces categories that look like findings.
 */
export function VerbatimsPage() {
  const canRead = useCan('survey:read')
  const canUpdate = useCan('survey:update')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [questionId, setQuestionId] = useState('')
  const [adding, setAdding] = useState(false)
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const versions = useLoad(
    () => (pollId ? listVersions(pollId) : Promise.resolve([])), [pollId])
  const versionId = (versions.data ?? []).slice()
    .sort((a, b) => b.versionNo - a.versionNo)[0]?.id ?? null
  const instrument = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])
  const frame = useLoad(
    () => (pollId && questionId ? getCodeFrame(pollId, questionId) : Promise.resolve(null)),
    [pollId, questionId])

  if (!canRead) return <Forbidden what="verbatims" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
    setQuestionId('')
  }

  const openText = (instrument.data?.questions ?? [])
    .filter((q) => q.typeCode === 'OPEN_TEXT')

  async function submitCode() {
    if (!pollId || !questionId) return
    setBusy(true)
    setError(null)
    try {
      await addCode(pollId, {
        questionId,
        code: code.trim().toUpperCase(),
        label: label.trim(),
        position: (frame.data?.codes.length ?? 0) + 1,
      })
      setAdding(false)
      setCode('')
      setLabel('')
      frame.reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const data = frame.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Verbatims"
        description="A human-defined coding frame, and how well the coders agree."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      <ErrorStrip message={error ?? frame.error ?? instrument.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll.</Note>
      ) : instrument.loading ? <LoadingBlock label="Loading the instrument…" /> : (
        <>
          <Panel title="Open-text question">
            {openText.length === 0 ? (
              <Note tone="info">
                This instrument has no open-text questions, so there is nothing to code. A
                coding frame belongs to an open-text question — a choice question's categories
                are already its options.
              </Note>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1.5 text-erp-xs">
                  Question
                  <select className="erp-input h-9 min-w-[280px]" value={questionId}
                    onChange={(e) => setQuestionId(e.target.value)}>
                    <option value="">Choose…</option>
                    {openText.map((q) => (
                      <option key={q.id} value={q.id}>{q.code} · {q.text}</option>
                    ))}
                  </select>
                </label>
                {questionId && canUpdate && (
                  <button type="button" className="erp-btn-secondary h-9 text-erp-sm"
                    onClick={() => setAdding(true)}>
                    Add a code
                  </button>
                )}
              </div>
            )}
          </Panel>

          {questionId && data && (
            <>
              <KpiStrip>
                <MetricCard label="Verbatims" value={count(data.verbatimCount)}
                  sub="open-text answers collected" />
                <MetricCard label="Coded" value={count(data.codedCount)}
                  sub={data.verbatimCount === 0 ? 'nothing to code yet'
                    : `${pct(data.codedCount / data.verbatimCount, 0)} of them`} />
                <MetricCard label="Double coded" value={count(data.doubleCodedCount)}
                  sub="the basis of the agreement figure" />
                <MetricCard label="Agreement"
                  value={data.agreement === null ? '—' : pct(data.agreement)}
                  sub={data.agreement === null ? 'nothing coded twice' : 'raw, not chance-corrected'}
                  accent={data.agreement !== null && Number(data.agreement) < 0.7} />
                <MetricCard label="Codes" value={count(data.codes.length)} sub="in the frame" />
              </KpiStrip>

              {data.agreementNote && <Note tone="info">{data.agreementNote}</Note>}

              <Panel title="The frame" description="Hierarchical, because real coding frames are.">
                <ErpTable head={(
                  <>
                    <Th>Code</Th>
                    <Th>Label</Th>
                    <Th className="text-right">Answers</Th>
                    <Th className="text-right">Share</Th>
                  </>
                )}>
                  {data.codes.length === 0 ? (
                    <EmptyRow colSpan={4} message={
                      'No codes yet. Read a sample of the verbatims first and let the frame come '
                      + 'from them — a frame written before anybody has read an answer is a '
                      + 'frame that fits nothing.'
                    } />
                  ) : data.codes.map((c) => (
                    <tr key={c.id} className="hover:bg-muted/40">
                      <Td className="font-mono text-erp-xs">
                        {c.parentId && <span className="text-muted-foreground">↳ </span>}
                        {c.code}
                      </Td>
                      <Td>{c.label}</Td>
                      <Td className="text-right tabular-nums">{count(c.codedAnswers)}</Td>
                      <Td className="text-right tabular-nums text-muted-foreground">
                        {data.codedCount === 0 ? '—' : pct(c.codedAnswers / data.codedCount, 0)}
                      </Td>
                    </tr>
                  ))}
                </ErpTable>
              </Panel>

              <Note tone="info">
                Export the verbatims with their codes from the Exports screen — VERBATIM_CSV
                carries one row per answer with the codes applied, and a cell beginning with an
                equals sign is neutralised so a spreadsheet does not execute a respondent's
                answer.
              </Note>
            </>
          )}
        </>
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a code"
        description="Codes are upper case and stable — they are what the export and the frequency table join on."
        footer={(
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="erp-btn-ghost h-9 text-erp-sm"
              onClick={() => setAdding(false)}>Cancel</button>
            <button type="button" className="erp-btn-primary h-9 text-erp-sm"
              disabled={busy || !code.trim() || !label.trim()} onClick={submitCode}>
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}
      >
        <div className="space-y-3">
          <Field label="Code" required>
            <input className="erp-input h-9 w-full font-mono" value={code}
              onChange={(e) => setCode(e.target.value)} placeholder="ROADS" />
          </Field>
          <Field label="Label" required>
            <input className="erp-input h-9 w-full" value={label}
              onChange={(e) => setLabel(e.target.value)} placeholder="Roads and transport" />
          </Field>
        </div>
      </Modal>
    </div>
  )
}
